import { useEffect, useRef, useState } from 'react';

// The A2UI action channel is fire-and-forget: the host does the presign +
// stream but never signals us when it's done. So the busy state is a short
// local acknowledgement of the click — it disables the control long enough to
// cover the presign and block a double-click (which would fire a second
// presign), then the browser's native download UI takes over.
export const DOWNLOAD_FEEDBACK_MS = 1000;

export interface DownloadFeedback {
  readonly isDownloading: boolean;
  /** Fires `onDownload`, or falls back to opening the URL when it's unbound. */
  readonly startDownload: () => void;
}

/** Click handling shared by every generated-file download control.
 *
 *  Extracted from `FileArtifactCard` so `ArtifactRow` doesn't carry a second
 *  copy of the same timer/guard/fallback logic. */
export function useDownloadFeedback(
  downloadUrl: string,
  onDownload?: () => void | Promise<void>
): DownloadFeedback {
  const [isDownloading, setIsDownloading] = useState(false);
  const feedbackTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(feedbackTimer.current), []);

  const startDownload = () => {
    if (isDownloading) return; // ignore repeat clicks while a download is in flight
    if (!onDownload) {
      window.open(downloadUrl, '_blank', 'noopener');
      return;
    }
    // Kick off the download; swallow any rejection so it can't surface as an
    // unhandled promise (the host handles/reports its own errors). We can't key
    // the busy state off completion — the action channel returns void — so it's
    // driven by a fixed timer.
    Promise.resolve(onDownload()).catch(() => undefined);
    setIsDownloading(true);
    clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(
      () => setIsDownloading(false),
      DOWNLOAD_FEEDBACK_MS
    );
  };

  return { isDownloading, startDownload };
}
